import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import GlobalStyles, { CommonClasses } from "../global/GlobalStyle";
import {
  MdTrendingUp,
  MdAccountBalance,
  MdLock,
  MdShield,
  MdArrowForward,
  MdSecurity,
  MdShowChart,
  MdAccountBalanceWallet,
  MdAutoGraph,
} from "react-icons/md";
import AOS from "aos";
import "aos/dist/aos.css";
import { CountUp } from "countup.js";
import avatarWithLedger from "../assets/images/image-1.png";
import logo from "../assets/logo.png";
import { ReactTyped } from "react-typed";

// Define proper TypeScript interfaces
interface HeroSlide {
  title: string;
  highlight: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  delay: number;
}

interface Stat {
  value: number;
  label: string;
  suffix: string;
}

interface Step {
  step: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Type the React Icons properly
const TrendIcon = MdTrendingUp as React.ComponentType<{ className?: string }>;
const BalanceIcon = MdAccountBalance as React.ComponentType<{
  className?: string;
}>;
const LockIcon = MdLock as React.ComponentType<{ className?: string }>;
const ShieldIcon = MdShield as React.ComponentType<{ className?: string }>;
const ArrowIcon = MdArrowForward as React.ComponentType<{ className?: string }>;
const SecurityIcon = MdSecurity as React.ComponentType<{ className?: string }>;
const ChartIcon = MdShowChart as React.ComponentType<{ className?: string }>;
const WalletIcon = MdAccountBalanceWallet as React.ComponentType<{
  className?: string;
}>;
const GraphIcon = MdAutoGraph as React.ComponentType<{ className?: string }>;

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsAnimated, setStatsAnimated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3500);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    AOS.init();
    AOS.refresh();
    // eslint-disable-next-line
  }, []);

  // Hero carousel slides
  const heroSlides: HeroSlide[] = [
    {
      title: "Secure Your Crypto Assets with",
      highlight: "Intelligent Protection",
      description:
        "OFS Security Ledger provides advanced market protection, automatically securing your investments when markets decline and optimizing gains during growth.",
      icon: ShieldIcon,
      color: "from-blue-500 to-purple-600",
    },
    {
      title: "Smart Market Protection with",
      highlight: "AI-Powered Algorithms",
      description:
        "Our intelligent system monitors market conditions 24/7, activating protection during downturns and maximizing profits during uptrends.",
      icon: GraphIcon,
      color: "from-green-500 to-blue-600",
    },
    {
      title: "Dual Balance System for",
      highlight: "Optimal Flexibility",
      description:
        "Manage your funds with our innovative ledger and stake balances, giving you complete control over your investment strategy.",
      icon: BalanceIcon,
      color: "from-purple-500 to-pink-600",
    },
    {
      title: "Bank-Level Security with",
      highlight: "Military-Grade Encryption",
      description:
        "Rest easy knowing your assets are protected by 256-bit encryption, multi-signature wallets, and institutional-grade security protocols.",
      icon: SecurityIcon,
      color: "from-orange-500 to-red-600",
    },
  ];

  const features: Feature[] = [
    {
      icon: ShieldIcon,
      title: "Advanced Security",
      description:
        "Bank-level security with multi-layer protection for your digital assets",
      delay: 100,
    },
    {
      icon: TrendIcon,
      title: "Smart Market Protection",
      description:
        "Automatically protect your investments during market downturns",
      delay: 200,
    },
    {
      icon: BalanceIcon,
      title: "Dual Balance System",
      description:
        "Manage your funds with ledger and stake balances for optimal flexibility",
      delay: 300,
    },
    {
      icon: LockIcon,
      title: "Asset Protection",
      description:
        "Lock in gains and minimize losses with our intelligent trading system",
      delay: 400,
    },
    {
      icon: ChartIcon,
      title: "Real-Time Analytics",
      description:
        "Comprehensive dashboard with live market data and performance metrics",
      delay: 500,
    },
    {
      icon: WalletIcon,
      title: "Multi-Wallet Support",
      description:
        "Connect multiple wallets and manage all your assets in one secure platform",
      delay: 600,
    },
  ];

  // eslint-disable-next-line
  const stats: Stat[] = [
    { value: 99.9, label: "Uptime Reliability", suffix: "%" },
    { value: 256, label: "Encryption", suffix: "-bit" },
    { value: 24, label: "Monitoring", suffix: "/7" },
    { value: 12476, label: "Protected Assets", suffix: "+" },
    { value: 50, label: "Supported Countries", suffix: "+" },
    { value: 100, label: "Transaction Speed", suffix: "ms" },
  ];

  const steps: Step[] = [
    {
      step: "1",
      title: "Connect & Deposit",
      description:
        "Connect your wallet and deposit cryptocurrency, which is converted to USD value in our secure system.",
      icon: WalletIcon,
    },
    {
      step: "2",
      title: "Automatic Protection",
      description:
        "Our AI system monitors market conditions and automatically activates protection during downturns.",
      icon: ShieldIcon,
    },
    {
      step: "3",
      title: "Grow & Withdraw",
      description:
        "Withdraw your protected funds anytime, with added gains from our intelligent market strategies.",
      icon: TrendIcon,
    },
  ];

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  // Hero carousel auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Stats counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !statsAnimated) {
            setStatsAnimated(true);
            stats.forEach((stat, index) => {
              const element = document.getElementById(`stat-value-${index}`);
              if (element) {
                const countUp = new CountUp(element, stat.value, {
                  duration: 2,
                  suffix: stat.suffix,
                });
                if (!countUp.error) {
                  countUp.start();
                } else {
                  console.error(countUp.error);
                }
              }
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        // eslint-disable-next-line
        observer.unobserve(statsRef.current);
      }
    };
  }, [statsAnimated, stats]);

  if (isLoading) {
    return (
      <div
        className={`${CommonClasses.transition} h-screen flex flex-col gap-10 items-center justify-center z-50 inset-0 bg-lightBackground dark:bg-darkBackground p-10`}
      >
        <img src={logo} alt="" className="w-28 h-28 animate-pulse" />
        <h1 className="text-2xl md:text-4xl font-bold leading-tight text-primary capitalize text-center">
          Welcome to <br />
          <ReactTyped
            strings={["Oracle financial services"]}
            className="text-secondary"
            typeSpeed={40}
          />
          <br /> security ledger
        </h1>
        <div
          className={`${CommonClasses.transition} animate-spin rounded-full h-12 w-12 border-2 border-b-transparent border-secondary`}
        ></div>
      </div>
    );
  }

  return (
    <div className={`${GlobalStyles.container} !p-0 !pt-16`}>
      {/* Enhanced Hero Section with Carousel */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Gradient Animation */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={`absolute -inset-10 bg-gradient-to-r ${heroSlides[activeSlide].color} transition-all duration-1000 transform scale-110`}
          ></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        {/* Floating Crypto Symbols - Larger and More Visible */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Bitcoin */}
          <div className="absolute top-1/4 left-10 text-white/20 text-6xl animate-float">
            ₿
          </div>
          <div className="absolute top-3/4 right-20 text-white/20 text-5xl animate-float-delayed">
            ₿
          </div>
          <div className="absolute top-1/3 right-1/4 text-white/20 text-7xl animate-float-slow">
            ₿
          </div>

          {/* Ethereum */}
          <div className="absolute top-1/5 right-10 text-white/20 text-6xl animate-float">
            Ξ
          </div>
          <div className="absolute bottom-1/4 left-1/3 text-white/20 text-7xl animate-float-delayed">
            Ξ
          </div>

          {/* General Crypto Symbols */}
          <div className="absolute top-10 left-1/4 text-white/20 text-5xl animate-float-slow">
            ⧫
          </div>
          <div className="absolute bottom-20 right-1/3 text-white/20 text-6xl animate-float">
            ◆
          </div>
          <div className="absolute top-2/3 left-20 text-white/20 text-5xl animate-float-delayed">
            ◈
          </div>
          <div className="absolute top-1/2 right-40 text-white/20 text-7xl animate-float-slow">
            ◎
          </div>

          {/* Additional Crypto Symbols for better coverage */}
          <div className="absolute top-1/6 left-1/2 text-white/20 text-6xl animate-float">
            $
          </div>
          <div className="absolute bottom-1/3 right-10 text-white/20 text-5xl animate-float-delayed">
            ●
          </div>
          <div className="absolute top-3/4 left-1/4 text-white/20 text-7xl animate-float-slow">
            ♦
          </div>
          <div className="absolute top-10 right-1/4 text-white/20 text-6xl animate-float">
            ◊
          </div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div
              className="text-white text-center lg:text-left"
              data-aos="fade-right"
            >
              <div className="mb-6">
                <div className="w-20 h-20 bg-white/20 rounded-2xl backdrop-blur-sm flex items-center justify-center mx-auto lg:mx-0 mb-6 max-sm:mt-6">
                  {React.createElement(heroSlides[activeSlide].icon, {
                    className: "w-10 h-10 text-white",
                  })}
                </div>

                <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
                  {heroSlides[activeSlide].title}{" "}
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                    {heroSlides[activeSlide].highlight}
                  </span>
                </h1>

                <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                  {heroSlides[activeSlide].description}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/register"
                  className="bg-white text-gray-900 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center justify-center"
                >
                  Get Started Free
                  <ArrowIcon className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/about"
                  className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm flex items-center justify-center"
                >
                  About Us
                </Link>
              </div>

              {/* Carousel Indicators */}
              <div className="flex justify-center lg:justify-start space-x-2 mt-12">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeSlide
                        ? "bg-white scale-125"
                        : "bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Image Content */}
            <div className="relative" data-aos="fade-left" data-aos-delay="200">
              <div className="relative z-10">
                <img
                  src={avatarWithLedger}
                  alt="Secure Crypto Trading"
                  className="w-full max-w-lg mx-auto transform hover:scale-105 transition-transform duration-500"
                />

                {/* Floating Elements */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-yellow-400/20 rounded-full blur-xl"></div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-400/20 rounded-full blur-xl"></div>

                {/* Stats Cards */}
                <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 shadow-2xl">
                  <div className="text-white text-center">
                    <div className="text-2xl font-bold">$2.1B+</div>
                    <div className="text-sm opacity-80">Assets Protected</div>
                  </div>
                </div>

                <div className="absolute -top-6 -right-6 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 shadow-2xl">
                  <div className="text-white text-center">
                    <div className="text-2xl font-bold">50K+</div>
                    <div className="text-sm opacity-80">Active Users</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Stats Section */}
      <section
        ref={statsRef}
        className="py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-3xl -mt-20 relative z-20 mx-4"
        data-aos="fade-up"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white mb-4">
              Trusted by Thousands Worldwide
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Join the revolution in secure cryptocurrency management and
              protection
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center group w-full h-full"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group-hover:border-primary border-2 border-transparent w-full h-full flex flex-col justify-center gap-1">
                  <div
                    id={`stat-value-${index}`}
                    className="text-2xl md:text-3xl font-bold text-primary mb-2"
                  >
                    {!statsAnimated ? "0" : stat.value}
                    {stat.suffix}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300 font-medium">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white mb-4">
              Why Choose{" "}
              <span className="text-primary">OFS Security Ledger</span>?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Our innovative platform combines cutting-edge security with
              intelligent market protection to safeguard your digital wealth
              like never before.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group"
                  data-aos="fade-up"
                  data-aos-delay={feature.delay}
                >
                  <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border-2 border-transparent group-hover:border-primary">
                    <div className="w-16 h-16 bg-gradient-to-r from-primary to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enhanced How It Works Section */}
      <section className="p-5">
        <div className="py-20 bg-gradient-to-br from-primary to-purple-600 rounded-3xl text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16" data-aos="fade-up">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                How It Works in 3 Simple Steps
              </h2>
              <p className="text-xl opacity-90 max-w-2xl mx-auto">
                Start protecting your crypto assets in minutes with our
                streamlined process
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {/* Connecting Line */}
              <div className="hidden md:block absolute top-20 left-1/3 right-1/3 h-1 bg-white/30 transform -translate-y-1/2"></div>

              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div
                    key={index}
                    className="text-center relative z-10"
                    data-aos="zoom-in"
                    data-aos-delay={index * 200}
                  >
                    <div className="bg-white/20 backdrop-blur-md rounded-3xl p-8 border border-white/30 hover:bg-white/30 transition-all duration-300 transform hover:scale-105">
                      <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-black font-bold text-sm">
                          {step.step}
                        </div>
                        <Icon className="w-10 h-10 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                      <p className="opacity-90 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="p-5">
        <div
          className="bg-gradient-to-r from-primary to-purple-600 rounded-3xl text-white text-center p-12 relative overflow-hidden py-20"
          data-aos="zoom-in"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-white rounded-full"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-white rounded-full"></div>
          </div>

          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Secure Your Crypto Future?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Join thousands of investors who trust OFS Security Ledger with
              their digital wealth. Start with $10 and experience the
              difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="bg-white text-gray-900 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center"
              >
                Start Protecting Today
                <ArrowIcon className="w-5 h-5 ml-2" />
              </Link>
            </div>
            <p className="text-sm opacity-80 mt-6">
              No credit card required • 30-day money-back guarantee
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
